'use client';

import React, { createContext, useContext, useState, useRef, useCallback, useEffect, ReactNode } from 'react';
import { StableSignalingClient, SignalingMessage, createStableSignalingClient } from '@/utils/stableSignaling';
import { createPeerConnectionWithHandlers, getUserMedia, startCallDuration, stopCallDuration, createFallbackRTCConfiguration } from '@/utils/webrtc';

// Call states
export type CallState = 'idle' | 'initiating' | 'ringing' | 'connecting' | 'connected' | 'ended';

export interface CallInfo {
  state: CallState;
  type: 'audio' | 'video' | null;
  isIncoming: boolean;
  callerId: string | null;
  callerName: string | null;
  callerPhoto?: string;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  isVideoOn: boolean;
  isSpeakerOn: boolean;
  callDuration: number;
  error: string | null;
}

export interface CallNotification {
  callerId: string;
  callerName: string;
  callerPhoto?: string;
  callType: 'audio' | 'video';
}

interface CallContextType {
  callInfo: CallInfo;
  callNotification: CallNotification | null;
  initiateCall: (type: 'audio' | 'video') => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => void;
  endCall: () => void;
  toggleMute: () => void;
  toggleVideo: () => void;
  toggleSpeaker: () => void;
  switchCamera: () => void;
  testAudioPermissions: () => Promise<void>;
  isCallActive: boolean;
  isIncomingCall: boolean;
  isOutgoingCall: boolean;
  // Additional properties for ChatWindow compatibility
  callType: 'audio' | 'video' | null;
  isIncoming: boolean;
  callerName: string | null;
  callerPhoto?: string;
  isConnected: boolean;
  isMuted: boolean;
  isVideoOn: boolean;
  isSpeakerOn: boolean;
  callDuration: number;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
}

export const CallContext = createContext<CallContextType | undefined>(undefined);

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};

interface CallProviderProps {
  children: ReactNode;
  userId: string;
  userName: string;
  userPhoto?: string;
}

export const CallProvider: React.FC<CallProviderProps> = ({ 
  children, 
  userId: currentUserId, 
  userName: currentUserName, 
  userPhoto: currentUserPhoto 
}) => {
  const [callInfo, setCallInfo] = useState<CallInfo>({
    state: 'idle',
    type: null,
    isIncoming: false,
    callerId: null,
    callerName: null,
    callerPhoto: undefined,
    localStream: null,
    remoteStream: null,
    isMuted: false,
    isVideoOn: true,
    isSpeakerOn: false,
    callDuration: 0,
    error: null
  });

  const [callNotification, setCallNotification] = useState<CallNotification | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(false);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const signalingClientRef = useRef<StableSignalingClient | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const callDurationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const offerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isEndingCallRef = useRef<boolean>(false);
  const lastEndCallTimeRef = useRef<number>(0);
  const isEndingDueToMessageRef = useRef<boolean>(false);
  const hasProcessedAnswerRef = useRef<boolean>(false);
  const hasProcessedOfferRef = useRef<boolean>(false);
  const signalingStateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  // Helper function to reset signaling state
  const resetSignalingState = useCallback(() => {
    hasProcessedAnswerRef.current = false;
    hasProcessedOfferRef.current = false;
    
    // Clear any signaling timeout
    if (signalingStateTimeoutRef.current) {
      clearTimeout(signalingStateTimeoutRef.current);
      signalingStateTimeoutRef.current = null;
    }
    
    // Clear pending ICE candidates
    pendingIceCandidatesRef.current = [];
  }, []);

  // Helper function to create a minimal SDP for last resort
  const createMinimalSdp = useCallback((originalSdp: string): string => {
    const lines = originalSdp.split('\n');
    const minimalLines = lines.filter(line => {
      // Keep only essential SDP lines - be very restrictive
      return line.startsWith('v=') || 
             line.startsWith('o=') || 
             line.startsWith('s=') || 
             line.startsWith('t=') || 
             line.startsWith('m=') || 
             line.startsWith('c=') || 
             line.startsWith('a=ice-') ||
             line.startsWith('a=fingerprint:') ||
             line.startsWith('a=setup:') ||
             line.startsWith('a=mid:') ||
             line.startsWith('a=sendrecv') ||
             line.startsWith('a=rtcp-mux') ||
             (line.startsWith('a=rtpmap:') && !line.includes('ulpfec/')) ||
             (line.startsWith('a=fmtp:') && !line.includes('repair-window=') && !line.includes('apt='));
    });
    
    return minimalLines.join('\n');
  }, [currentUserId]);

  // Helper function to normalize SDP line endings (minimal and safe)
  const normalizeSdp = useCallback((sdp: string): string => {
    if (!sdp) return sdp;
    
    try {
      // Only normalize line endings to CRLF as required by SDP spec
      // Don't modify any SDP content - just ensure proper line endings
      return sdp.replace(/\r?\n/g, '\r\n');
    } catch (error) {
      // Error normalizing SDP
      return sdp; // Return original if normalization fails
    }
  }, [currentUserId]);

  // Ensure consistent media line ordering (audio first, then video)
  const ensureConsistentMediaOrder = useCallback((sdp: string): string => {
    if (!sdp) return sdp;
    
    try {
      const lines = sdp.split('\r\n');
      const audioLines: string[] = [];
      const videoLines: string[] = [];
      const otherLines: string[] = [];
      
      let inAudioSection = false;
      let inVideoSection = false;
      
      for (const line of lines) {
        if (line.startsWith('m=audio')) {
          inAudioSection = true;
          inVideoSection = false;
          audioLines.push(line);
        } else if (line.startsWith('m=video')) {
          inAudioSection = false;
          inVideoSection = true;
          videoLines.push(line);
        } else if (line.startsWith('m=')) {
          // Other media types
          inAudioSection = false;
          inVideoSection = false;
          otherLines.push(line);
        } else if (inAudioSection) {
          audioLines.push(line);
        } else if (inVideoSection) {
          videoLines.push(line);
        } else {
          otherLines.push(line);
        }
      }
      
      // Reconstruct SDP with consistent ordering: audio first, then video
      const reorderedLines = [
        ...otherLines.filter(line => !line.startsWith('m=')),
        ...audioLines,
        ...videoLines,
        ...otherLines.filter(line => line.startsWith('m=') && !line.startsWith('m=audio') && !line.startsWith('m=video'))
      ];
      
      return reorderedLines.join('\r\n');
    } catch (error) {
      // Error reordering SDP media lines
      return sdp; // Return original if reordering fails
    }
  }, [currentUserId]);

  // SDP M-line validation is no longer needed as we use audio-only calls

  // Helper function to process pending ICE candidates
  const processPendingIceCandidates = useCallback(async () => {
    if (!peerConnectionRef.current || !pendingIceCandidatesRef.current.length) {
      return;
    }

    // Processing pending ICE candidates
    
    // Add a small delay to ensure peer connection is fully ready
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const candidates = [...pendingIceCandidatesRef.current];
    pendingIceCandidatesRef.current = [];

    for (const candidate of candidates) {
      try {
        // Double-check peer connection state before adding candidate
        if (peerConnectionRef.current && peerConnectionRef.current.signalingState !== 'closed') {
          await peerConnectionRef.current.addIceCandidate(candidate);
          // Processed pending ICE candidate successfully
        } else {
          // Peer connection is closed, skipping pending ICE candidate
        }
      } catch (error) {
        // Error processing pending ICE candidate
        if (error instanceof Error && error.name === 'InvalidStateError') {
          // Invalid state for pending ICE candidate
        }
      }
    }
  }, [currentUserId]);

  // Helper function to create peer connection with dynamic ICE servers
  const createPeerConnectionWithDynamicICE = useCallback(async (): Promise<RTCPeerConnection> => {
    try {
      return await createPeerConnectionWithHandlers({
        onIceCandidate: (event) => {
          if (event.candidate && signalingClientRef.current) {
            signalingClientRef.current.sendIceCandidate(event.candidate, callInfo.callerId || '');
          }
        },
        onTrack: (event) => {
          if (event.streams && event.streams[0]) {
            const remoteStream = event.streams[0];
            // Setting remote stream
            setCallInfo(prev => ({ ...prev, remoteStream: remoteStream }));
          }
        },
        onCallDurationStart: () => {
          startCallDuration(callDurationIntervalRef, setCallInfo);
        },
        currentUserId,
        isEndingCallRef,
        callInfoState: callInfo.state,
        callDurationIntervalRef,
        setCallInfo
      });
    } catch (error) {
      // Failed to create peer connection with dynamic ICE
      throw error;
    }
  }, [currentUserId, callInfo.state]);

  // Helper function to force reset peer connection if stuck
  const forceResetPeerConnection = useCallback(async () => {
    // Force resetting peer connection due to stuck state
    
    if (peerConnectionRef.current) {
      // Close existing connection
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    // Reset signaling state
    resetSignalingState();
    
    // Create new peer connection with dynamic ICE servers
    try {
      peerConnectionRef.current = await createPeerConnectionWithDynamicICE();
    } catch (error) {
      // Failed to create peer connection during reset
      // Fallback to static configuration
      const configuration = createFallbackRTCConfiguration();
      peerConnectionRef.current = new RTCPeerConnection(configuration);
    }

    // Add local stream if available
    if (localStreamRef.current && peerConnectionRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        if (!peerConnectionRef.current || !localStreamRef.current) {
          // Cannot add track: peer connection or local stream is null
          return;
        }
        peerConnectionRef.current.addTrack(track, localStreamRef.current);
      });
    }
  }, [currentUserId, resetSignalingState, createPeerConnectionWithDynamicICE]);

  // Store the message handler in a ref to prevent WebSocket recreation
  const messageHandlerRef = useRef<(message: SignalingMessage) => void>(() => {});

  const handleSignalingMessage = useCallback(async (message: SignalingMessage) => {
    // Always log important messages
    if (message.type === 'incoming-call' || message.type === 'call-ended' || message.type === 'call-accepted') {
      // Received signaling message
    }

    switch (message.type) {
      case 'incoming-call':
        const incomingCallMessage = message as SignalingMessage & {
          callerId: string;
          callerName: string;
          callerPhoto?: string;
          callType: 'audio' | 'video';
        };
        // Incoming call received
        setCallNotification({
          callerId: incomingCallMessage.callerId,
          callerName: incomingCallMessage.callerName,
          callerPhoto: incomingCallMessage.callerPhoto,
          callType: incomingCallMessage.callType
        });
        
        // Pre-create peer connection for incoming call to be ready for offer
        // Pre-creating peer connection for incoming call
        try {
          peerConnectionRef.current = await createPeerConnectionWithDynamicICE();
        } catch (error) {
          // Failed to create peer connection for incoming call
          // Fallback to static configuration
          const configuration = {
            iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
          };
          peerConnectionRef.current = new RTCPeerConnection(configuration);
          
          // Set up peer connection event handlers
          peerConnectionRef.current.onicecandidate = (event) => {
            if (event.candidate && signalingClientRef.current) {
              signalingClientRef.current.sendIceCandidate(event.candidate, callInfo.callerId || '');
            }
          };
        }

        peerConnectionRef.current.ontrack = (event) => {
          // ONTRACK EVENT TRIGGERED
          // Event details
          
          if (event.streams && event.streams[0]) {
            const remoteStream = event.streams[0];
            // REMOTE STREAM RECEIVED
            
            // Log detailed track information and fix muted tracks
            const remoteTracks = remoteStream.getTracks();
            if (remoteTracks && Array.isArray(remoteTracks)) {
              remoteTracks.forEach((track, index) => {
                // Track details before fix
                
                // Fix muted audio tracks - check if muted property is writable
                if (track.kind === 'audio' && track.muted) {
                  // Fixing muted audio track
                  try {
                    track.enabled = true;
                    // Track details after fix
                  } catch (error) {
                    // Cannot set muted property on track
                  }
                }
              });
            } else {
              // No tracks available in remote stream
            }
            
            // Setting remote stream in callInfo
            setCallInfo(prev => {
              // Previous callInfo
              const newCallInfo = { ...prev, remoteStream: remoteStream };
              // New callInfo
              return newCallInfo;
            });
            // REMOTE STREAM SET COMPLETE
          } else {
            // No streams in event or empty streams array
          }
        };

        peerConnectionRef.current.onconnectionstatechange = () => {
          // Don't log if we're ending the call or if call is already ended
          if (!isEndingCallRef.current && callInfo.state !== 'idle') {
            console.log(`[${currentUserId}] Connection state changed to:`, peerConnectionRef.current?.connectionState, 'at', new Date().toISOString());
          }
          if (peerConnectionRef.current?.connectionState === 'connected' && !isEndingCallRef.current) {
            setCallInfo(prev => ({ ...prev, state: 'connected' }));
            // Start call duration timer (clear any existing timer first)
            if (callDurationIntervalRef.current) {
              clearInterval(callDurationIntervalRef.current);
            }
            callDurationIntervalRef.current = setInterval(() => {
              setCallInfo(prev => ({ ...prev, callDuration: prev.callDuration + 1 }));
            }, 1000);
          }
        };

        // Add debugging for ICE connection state
        peerConnectionRef.current.oniceconnectionstatechange = () => {
          console.log(`[${currentUserId}] ICE connection state:`, peerConnectionRef.current?.iceConnectionState);
        };

        // Add debugging for ICE gathering state
        peerConnectionRef.current.onicegatheringstatechange = () => {
          console.log(`[${currentUserId}] ICE gathering state:`, peerConnectionRef.current?.iceGatheringState);
        };
        break;

      case 'call-accepted':
        const callAcceptedMessage = message as SignalingMessage & {
          calleeId: string;
        };
        console.log(`[${currentUserId}] Call accepted by:`, callAcceptedMessage.calleeId);
        // Store the target user ID for ICE candidates
        setCallInfo(prev => ({ ...prev, callerId: callAcceptedMessage.calleeId }));
        // Clear any existing offer timeout
        if (offerTimeoutRef.current) {
          clearTimeout(offerTimeoutRef.current);
        }
        // Send offer now that call is accepted
        offerTimeoutRef.current = setTimeout(async () => {
          try {
            // Ensure peer connection exists before creating offer
            if (!peerConnectionRef.current) {
              console.error(`[${currentUserId}] Cannot create offer: peer connection is null`);
              return;
            }
            
            console.log(`[${currentUserId}] Creating offer for accepted call (type: ${callInfo.type})`);
            let offer = await peerConnectionRef.current.createOffer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: callInfo.type === 'video'
            });
            console.log(`[${currentUserId}] Offer created:`, offer.type);
            console.log(`[${currentUserId}] Created offer SDP:`, offer.sdp?.substring(0, 200) + '...');
            
          // Ensure consistent media line ordering for video calls
          if (callInfo.type === 'video' && offer.sdp) {
            offer.sdp = ensureConsistentMediaOrder(offer.sdp);
            console.log(`[${currentUserId}] Reordered offer SDP for consistent media lines`);
          }
            
            try {
              if (!peerConnectionRef.current) {
                console.error(`[${currentUserId}] Cannot set local description: peer connection is null`);
                return;
              }
              await peerConnectionRef.current.setLocalDescription(offer);
              console.log(`[${currentUserId}] Local description set`);
            } catch (sdpError) {
              console.error(`[${currentUserId}] Error setting local description:`, sdpError);
              if (sdpError instanceof Error && sdpError.message.includes('Failed to parse SessionDescription')) {
                console.error(`[${currentUserId}] SDP parsing failed, offer SDP:`, offer.sdp?.substring(0, 200) + '...');
                console.error(`[${currentUserId}] Full SDP for debugging:`, offer.sdp);
                
                // Try to create a new offer with different constraints
                try {
                  console.log(`[${currentUserId}] Attempting to create new offer with different constraints...`);
                  
                  // Ensure peer connection exists before creating offer
                  if (!peerConnectionRef.current) {
                    console.error(`[${currentUserId}] Cannot create retry offer: peer connection is null`);
                    return;
                  }
                  
                  // Try with more restrictive constraints
                  const newOffer = await peerConnectionRef.current.createOffer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: callInfo.type === 'video'
              });
                  
                  const cleanedNewOffer = {
                    ...newOffer,
                    sdp: normalizeSdp(newOffer.sdp || '')
                  };
                  
                  console.log(`[${currentUserId}] New offer created and cleaned, attempting to set local description...`);
                  if (!peerConnectionRef.current) {
                    console.error(`[${currentUserId}] Cannot set local description: peer connection is null`);
                    return;
                  }
                  await peerConnectionRef.current.setLocalDescription(cleanedNewOffer);
                  console.log(`[${currentUserId}] Retry with new offer successful`);
                  offer = cleanedNewOffer; // Use the new offer for signaling
                } catch (retryError) {
                  console.error(`[${currentUserId}] Retry with new offer also failed:`, retryError);
                  console.error(`[${currentUserId}] Retry error details:`, retryError instanceof Error ? retryError.message : String(retryError));
                  
                  // If retry also fails, try one more time with minimal constraints
                  try {
                    console.log(`[${currentUserId}] Attempting final retry with minimal constraints...`);
                    
                    // Ensure peer connection exists before creating offer
                    if (!peerConnectionRef.current) {
                      console.error(`[${currentUserId}] Cannot create minimal offer: peer connection is null`);
                      return;
                    }
                    
                    const minimalOffer = await peerConnectionRef.current.createOffer({
                      offerToReceiveAudio: true,
                      offerToReceiveVideo: callInfo.type === 'video'
                    });
                    
                    const cleanedMinimalOffer = {
                      ...minimalOffer,
                      sdp: normalizeSdp(minimalOffer.sdp || '')
                    };
                    
                    if (!peerConnectionRef.current) {
                      console.error(`[${currentUserId}] Cannot set local description: peer connection is null`);
                      return;
                    }
                    await peerConnectionRef.current.setLocalDescription(cleanedMinimalOffer);
                    console.log(`[${currentUserId}] Final retry with minimal constraints successful`);
                    offer = cleanedMinimalOffer;
                  } catch (finalError) {
                    console.error(`[${currentUserId}] All retry attempts failed:`, finalError);
                    
                    // Last resort: try to create a completely minimal SDP
                    try {
                      console.log(`[${currentUserId}] Attempting last resort with minimal SDP...`);
                      
                      // Ensure peer connection exists before creating offer
                      if (!peerConnectionRef.current) {
                        console.error(`[${currentUserId}] Cannot create last resort offer: peer connection is null`);
                        return;
                      }
                      
                      const lastResortOffer = await peerConnectionRef.current.createOffer({
                        offerToReceiveAudio: true,
                        offerToReceiveVideo: callInfo.type === 'video'
                      });
                      
                      // Create a completely minimal SDP
                      const minimalSdp = createMinimalSdp(lastResortOffer.sdp || '');
                      const minimalOffer = {
                        ...lastResortOffer,
                        sdp: minimalSdp
                      };
                      
                      if (!peerConnectionRef.current) {
                        console.error(`[${currentUserId}] Cannot set local description: peer connection is null`);
                        return;
                      }
                      await peerConnectionRef.current.setLocalDescription(minimalOffer);
                      console.log(`[${currentUserId}] Last resort minimal SDP successful`);
                      offer = minimalOffer;
                    } catch (lastResortError) {
                      console.error(`[${currentUserId}] All attempts including last resort failed:`, lastResortError);
                      throw sdpError; // Re-throw original error
                    }
                  }
                }
              } else {
                throw sdpError; // Re-throw if not SDP parsing error
              }
            }
            
            if (signalingClientRef.current && peerConnectionRef.current?.localDescription) {
              console.log(`[${currentUserId}] Sending offer via signaling to ${callAcceptedMessage.calleeId}`);
              console.log(`[${currentUserId}] Current callInfo:`, callInfo);
              console.log(`[${currentUserId}] Offer SDP before sending:`, offer.sdp?.substring(0, 200) + '...');
              // Send the full RTCLocalDescription object directly
              const success = signalingClientRef.current.sendOffer(peerConnectionRef.current.localDescription, callAcceptedMessage.calleeId);
              console.log(`[${currentUserId}] Offer send result:`, success);
            } else {
              console.error(`[${currentUserId}] Cannot send offer: signaling client or peer connection local description not available`);
            }
          } catch (error) {
            console.error(`[${currentUserId}] Error creating offer:`, error);
          }
        }, 500); // Short delay to ensure receiver is ready
        break;

      case 'call-ended':
        console.log(`[${currentUserId}] Call ended - current state:`, callInfo.state, 'isEnding:', isEndingCallRef.current);
        console.log(`[${currentUserId}] Processing call-ended message, calling endCall`);
        isEndingDueToMessageRef.current = true;
        endCall();
        break;

      case 'offer':
        console.log(`[${currentUserId}] Received offer message:`, message);
        // Validate message structure
        if (!message.type || !message.sdp) {
          console.error(`[${currentUserId}] Invalid offer message: missing type or sdp`, message);
          return;
        }
        // Parse JSON and construct RTCSessionDescription with proper CRLF normalization
        const offerDesc = new RTCSessionDescription({
          type: message.type as RTCSdpType,
          sdp: (message.sdp as string).replace(/\r?\n/g, '\r\n')
        });
        handleOffer(offerDesc, message.from as string);
        break;

      case 'answer':
        console.log(`[${currentUserId}] Received answer message:`, message);
        // Validate message structure
        if (!message.type || !message.sdp) {
          console.error(`[${currentUserId}] Invalid answer message: missing type or sdp`, message);
          return;
        }
        // Parse JSON and construct RTCSessionDescription with proper CRLF normalization
        const answerDesc = new RTCSessionDescription({
          type: message.type as RTCSdpType,
          sdp: (message.sdp as string).replace(/\r?\n/g, '\r\n')
        });
        handleAnswer(answerDesc);
        break;

      case 'ice-candidate':
        console.log(`[${currentUserId}] Received ICE candidate`);
        handleIceCandidate(message.candidate as RTCIceCandidateInit);
        break;

      default:
        console.log(`[${currentUserId}] Unknown message type:`, message.type);
    }
  }, [currentUserId]);

  // Store the message handler in the ref
  useEffect(() => {
    messageHandlerRef.current = handleSignalingMessage;
  }, [handleSignalingMessage]);

  // Initialize signaling client on mount
  useEffect(() => {
    // COMMENTED OUT: Signaling client disabled to prevent WebSocket connection errors
    // Uncomment when signaling server is available
    /*
    // Prevent multiple initializations
    if (signalingClientRef.current) {
      console.log(`[${currentUserId}] Signaling client already initialized`);
      return;
    }

    console.log(`[${currentUserId}] Initializing signaling client at`, new Date().toISOString());
    
    const client = createStableSignalingClient(
      currentUserId,
      'default-room',
      (message: SignalingMessage) => messageHandlerRef.current?.(message)
    );
    
    signalingClientRef.current = client;
    */

    return () => {
      //console.log(`[${currentUserId}] Cleaning up signaling client at`, new Date().toISOString());
      
      // Clear any active timers
      if (callDurationIntervalRef.current) {
        clearInterval(callDurationIntervalRef.current);
        callDurationIntervalRef.current = null;
      }

      // Clear offer timeout
      if (offerTimeoutRef.current) {
        clearTimeout(offerTimeoutRef.current);
        offerTimeoutRef.current = null;
      }
      
      // COMMENTED OUT: Signaling client cleanup disabled
      /*
      if (signalingClientRef.current) {
        signalingClientRef.current.disconnect();
        signalingClientRef.current = null;
      }
      */
    };
  }, [currentUserId]);

  const handleOffer = useCallback(async (offer: RTCSessionDescriptionInit, callerId?: string) => {
    console.log(`[${currentUserId}] Handling offer, peer connection exists:`, !!peerConnectionRef.current);
    console.log(`[${currentUserId}] Offer caller ID:`, callerId);
    console.log(`[${currentUserId}] Current callInfo.callerId:`, callInfo.callerId);
    console.log(`[${currentUserId}] Received offer SDP:`, offer.sdp?.substring(0, 200) + '...');
    console.log(`[${currentUserId}] Offer type:`, offer.type);
    
    // Use the passed callerId or fall back to callInfo.callerId
    const targetCallerId = callerId || callInfo.callerId || '';
    console.log(`[${currentUserId}] Using target caller ID:`, targetCallerId);
    
    // Always create a fresh peer connection for offers to avoid state conflicts
    if (peerConnectionRef.current) {
      console.log(`[${currentUserId}] Closing existing peer connection for fresh offer handling`);
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    console.log(`[${currentUserId}] Creating fresh peer connection for offer`);
    let peerConnectionCreated = false;
    
    try {
      peerConnectionRef.current = await createPeerConnectionWithDynamicICE();
      peerConnectionCreated = true;
      console.log(`[${currentUserId}] Peer connection created successfully for offer:`, !!peerConnectionRef.current);
    } catch (error) {
      console.error(`[${currentUserId}] Failed to create peer connection for offer:`, error);
      // Fallback to static configuration
      const configuration = createFallbackRTCConfiguration();
      peerConnectionRef.current = new RTCPeerConnection(configuration);
      
      // Set up peer connection event handlers
      peerConnectionRef.current.onicecandidate = (event) => {
        if (event.candidate && signalingClientRef.current) {
          signalingClientRef.current.sendIceCandidate(event.candidate, targetCallerId);
        }
      };
      peerConnectionCreated = true;
      console.log(`[${currentUserId}] Fallback peer connection created:`, !!peerConnectionRef.current);
    }
    
    // Verify peer connection was created successfully
    if (!peerConnectionCreated || !peerConnectionRef.current) {
      console.error(`[${currentUserId}] Failed to create peer connection for offer - aborting`);
      return;
    }

    try {
      peerConnectionRef.current.ontrack = (event) => {
        console.log(`[${currentUserId}] ===== ONTRACK EVENT TRIGGERED (HANDLE OFFER) =====`);
        console.log(`[${currentUserId}] Event details:`, event);
        console.log(`[${currentUserId}] Event streams:`, event.streams);
        console.log(`[${currentUserId}] Event track:`, event.track);
        
        if (event.streams && event.streams[0]) {
          const remoteStream = event.streams[0];
          console.log(`[${currentUserId}] ===== REMOTE STREAM RECEIVED (HANDLE OFFER) =====`);
          console.log(`[${currentUserId}] Stream ID:`, remoteStream.id);
          console.log(`[${currentUserId}] Stream active:`, remoteStream.active);
          console.log(`[${currentUserId}] Total tracks:`, remoteStream.getTracks().length);
          console.log(`[${currentUserId}] Audio tracks:`, remoteStream.getAudioTracks().length);
          console.log(`[${currentUserId}] Video tracks:`, remoteStream.getVideoTracks().length);
          
          // Log detailed track information and fix muted tracks
          const remoteTracks = remoteStream.getTracks();
          if (remoteTracks && Array.isArray(remoteTracks)) {
            remoteTracks.forEach((track, index) => {
              console.log(`[${currentUserId}] Track ${index} (handle offer, before fix):`, {
                kind: track.kind,
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState,
                label: track.label,
                id: track.id
              });
              
              // Fix muted audio tracks - check if muted property is writable
              if (track.kind === 'audio' && track.muted) {
                console.log(`[${currentUserId}] Fixing muted audio track ${index} (handle offer)`);
                try {
                  track.enabled = true;
                  console.log(`[${currentUserId}] Track ${index} (handle offer, after fix):`, {
                    kind: track.kind,
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState,
                    label: track.label,
                    id: track.id
                  });
                } catch (error) {
                  console.warn(`[${currentUserId}] Cannot set muted property on track ${index}:`, error);
                }
              }
            });
          } else {
            console.warn(`[${currentUserId}] No tracks available in remote stream or getTracks() returned invalid data:`, remoteTracks);
          }
          
          console.log(`[${currentUserId}] Setting remote stream in callInfo (handle offer)...`);
          setCallInfo(prev => {
            console.log(`[${currentUserId}] Previous callInfo (handle offer):`, prev);
            const newCallInfo = { ...prev, remoteStream: remoteStream };
            console.log(`[${currentUserId}] New callInfo (handle offer):`, newCallInfo);
            return newCallInfo;
          });
          console.log(`[${currentUserId}] ===== REMOTE STREAM SET COMPLETE (HANDLE OFFER) =====`);
        } else {
          console.log(`[${currentUserId}] No streams in event or empty streams array (handle offer)`);
        }
      };

      peerConnectionRef.current.onconnectionstatechange = () => {
        if (!isEndingCallRef.current && callInfo.state !== 'idle') {
          console.log(`[${currentUserId}] Connection state changed to:`, peerConnectionRef.current?.connectionState, 'at', new Date().toISOString());
        }
        if (peerConnectionRef.current?.connectionState === 'connected' && !isEndingCallRef.current) {
          setCallInfo(prev => ({ ...prev, state: 'connected' }));
          if (callDurationIntervalRef.current) {
            clearInterval(callDurationIntervalRef.current);
          }
          callDurationIntervalRef.current = setInterval(() => {
            setCallInfo(prev => ({ ...prev, callDuration: prev.callDuration + 1 }));
          }, 1000);
        }
      };

      // Add debugging for ICE connection state
      peerConnectionRef.current.oniceconnectionstatechange = () => {
        console.log(`[${currentUserId}] ICE connection state:`, peerConnectionRef.current?.iceConnectionState);
      };

      // Add debugging for ICE gathering state
      peerConnectionRef.current.onicegatheringstatechange = () => {
        console.log(`[${currentUserId}] ICE gathering state:`, peerConnectionRef.current?.iceGatheringState);
      };
      
      console.log(`[${currentUserId}] Peer connection created for offer handling`);
      
      // Add local stream if it exists
      if (localStreamRef.current && peerConnectionRef.current) {
        console.log(`[${currentUserId}] Adding local stream to peer connection (handle offer)`);
        const localTracks = localStreamRef.current.getTracks();
        if (localTracks && Array.isArray(localTracks)) {
          localTracks.forEach(track => {
            try {
              if (!peerConnectionRef.current || !localStreamRef.current) {
                console.error(`[${currentUserId}] Cannot add track: peer connection or local stream is null`);
                return;
              }
              peerConnectionRef.current.addTrack(track, localStreamRef.current);
              console.log(`[${currentUserId}] Added track to peer connection (handle offer):`, track.kind);
            } catch (error) {
              console.error(`[${currentUserId}] Error adding track to peer connection (handle offer):`, error);
            }
          });
        } else {
          console.warn(`[${currentUserId}] No tracks available in local stream or getTracks() returned invalid data:`, localTracks);
        }
      }
    } catch (error) {
      console.error(`[${currentUserId}] Error setting up peer connection for offer:`, error);
    }

    // Check if we've already processed an offer
    if (hasProcessedOfferRef.current) {
      console.warn(`[${currentUserId}] Offer already processed, ignoring duplicate`);
      return;
    }

    // Check if we're in a state where we can't process offers
    const currentState = peerConnectionRef.current.signalingState;
    if (currentState === 'have-remote-offer') {
      console.warn(`[${currentUserId}] Already have remote offer, cannot process another offer`);
      return;
    }

    // Add a small delay to ensure peer connection is fully initialized
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      // Check if peer connection exists
      if (!peerConnectionRef.current) {
        console.error(`[${currentUserId}] No peer connection available for offer processing`);
        console.error(`[${currentUserId}] This should not happen - peer connection was just created`);
        return;
      }
      
      console.log(`[${currentUserId}] Peer connection verified before offer processing:`, {
        exists: !!peerConnectionRef.current,
        signalingState: peerConnectionRef.current.signalingState,
        connectionState: peerConnectionRef.current.connectionState
      });
      
      // Check current signaling state
      const currentState = peerConnectionRef.current.signalingState;
      console.log(`[${currentUserId}] Current signaling state:`, currentState);
      
      // Comprehensive state validation for offer processing
      if (currentState === 'stable') {
        console.log(`[${currentUserId}] Connection is stable, processing offer for new call`);
        
        try {
          // Set the remote description (offer) - normalize SDP line endings
          // Create RTCSessionDescription with proper CRLF normalization
          const normalizedOffer = new RTCSessionDescription({
            type: offer.type,
            sdp: offer.sdp?.replace(/\r?\n/g, '\r\n') || ''
          });
          console.log(`[${currentUserId}] Setting remote description with normalized offer SDP:`, normalizedOffer.sdp.substring(0, 200) + '...');
          await peerConnectionRef.current.setRemoteDescription(normalizedOffer);
          hasProcessedOfferRef.current = true;
          
          console.log(`[${currentUserId}] Remote description set, creating answer`);
          
          // Create and set local description (answer) - use call type
          const isVideoCall = callInfo.type === 'video' || callNotification?.callType === 'video';
          const answer = await peerConnectionRef.current.createAnswer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: isVideoCall
          });
          console.log(`[${currentUserId}] Created answer SDP:`, answer.sdp?.substring(0, 200) + '...');
          
          // Ensure consistent media line ordering for video calls
          if (isVideoCall && answer.sdp) {
            answer.sdp = ensureConsistentMediaOrder(answer.sdp);
            console.log(`[${currentUserId}] Reordered answer SDP for consistent media lines`);
          }
          
          await peerConnectionRef.current.setLocalDescription(answer);
          
          // Send answer via signaling
          if (signalingClientRef.current) {
            console.log(`[${currentUserId}] Sending answer to ${callInfo.callerId}`);
            console.log(`[${currentUserId}] Current callInfo for answer:`, callInfo);
            console.log(`[${currentUserId}] Answer SDP before sending:`, answer.sdp?.substring(0, 200) + '...');
            // Send the full RTCLocalDescription object directly
            signalingClientRef.current.sendAnswer(peerConnectionRef.current!.localDescription!, targetCallerId);
          } else {
            console.error(`[${currentUserId}] No signaling client available for sending answer`);
          }
          
          console.log(`[${currentUserId}] Offer processed successfully in stable state`);
        } catch (error) {
          console.error(`[${currentUserId}] Error processing offer in stable state:`, error);
        }
        return;
      } else if (currentState === 'have-local-pranswer') {
        console.log(`[${currentUserId}] Setting remote description (have-local-pranswer)`);
        
        try {
          // Create RTCSessionDescription with proper CRLF normalization
          const normalizedOffer = new RTCSessionDescription({
            type: offer.type,
            sdp: offer.sdp?.replace(/\r?\n/g, '\r\n') || ''
          });
          console.log(`[${currentUserId}] Setting remote description (have-local-pranswer) with normalized SDP:`, normalizedOffer.sdp.substring(0, 200) + '...');
          await peerConnectionRef.current.setRemoteDescription(normalizedOffer);
          hasProcessedOfferRef.current = true;
          
          // Wait a moment to ensure remote description is set
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Verify remote description is set before creating answer
          if (!peerConnectionRef.current.remoteDescription) {
            console.error(`[${currentUserId}] Remote description is null after setting, cannot create answer`);
            console.error(`[${currentUserId}] Current signaling state:`, peerConnectionRef.current.signalingState);
            console.error(`[${currentUserId}] Connection state:`, peerConnectionRef.current.connectionState);
            console.error(`[${currentUserId}] ICE connection state:`, peerConnectionRef.current.iceConnectionState);
            return;
          }
          
          console.log(`[${currentUserId}] Remote description verified:`, {
            type: peerConnectionRef.current.remoteDescription.type,
            sdp: peerConnectionRef.current.remoteDescription.sdp ? 'present' : 'missing'
          });
          
          // Double-check signaling state before creating answer
          const finalState = peerConnectionRef.current.signalingState;
          console.log(`[${currentUserId}] Final signaling state before creating answer:`, finalState);
          
          if (finalState !== 'have-remote-offer') {
            console.error(`[${currentUserId}] Invalid signaling state for creating answer:`, finalState);
            console.error(`[${currentUserId}] Expected: have-remote-offer, Got:`, finalState);
            
            // If we're in stable state, we can't create an answer
            if (finalState === 'stable') {
              console.log(`[${currentUserId}] State is stable - cannot create answer, connection may already be established`);
              return;
            } else {
              console.error(`[${currentUserId}] Cannot proceed with answer creation in state:`, finalState);
              return;
            }
          }
          
          console.log(`[${currentUserId}] Creating answer`);
          const isVideoCall = callInfo.type === 'video' || callNotification?.callType === 'video';
          let answer: RTCSessionDescriptionInit;
          try {
            answer = await peerConnectionRef.current.createAnswer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: isVideoCall
            });
            
            // Clean the answer SDP and ensure consistent media ordering
            const cleanedSdp = normalizeSdp(answer.sdp || '');
            const finalSdp = isVideoCall ? ensureConsistentMediaOrder(cleanedSdp) : cleanedSdp;
            answer = {
              ...answer,
              sdp: finalSdp
            };
          } catch (error) {
            console.error(`[${currentUserId}] Error creating answer:`, error);
            if (error instanceof Error && error.name === 'InvalidStateError') {
              console.error(`[${currentUserId}] InvalidStateError: Remote description may not be properly set`);
              console.error(`[${currentUserId}] Current signaling state:`, peerConnectionRef.current.signalingState);
              console.error(`[${currentUserId}] Remote description:`, peerConnectionRef.current.remoteDescription);
            }
            return;
          }
          
          console.log(`[${currentUserId}] Setting local description`);
          await peerConnectionRef.current.setLocalDescription(answer);
          
          if (signalingClientRef.current) {
            console.log(`[${currentUserId}] Sending answer`);
            signalingClientRef.current.sendAnswer(peerConnectionRef.current!.localDescription!, targetCallerId);
          } else {
            console.error(`[${currentUserId}] No signaling client available`);
          }
        } catch (error) {
          console.error(`[${currentUserId}] Error setting remote description:`, error);
          return;
        }
      } else if (currentState === 'have-remote-offer') {
        console.warn(`[${currentUserId}] Already have remote offer, checking if this is a duplicate`);
        
        // Check if the offer is the same as what we already have
        const currentRemoteDescription = peerConnectionRef.current.remoteDescription;
        if (currentRemoteDescription && currentRemoteDescription.sdp === offer.sdp) {
          console.log(`[${currentUserId}] Duplicate offer detected, ignoring`);
          hasProcessedOfferRef.current = true;
          return;
        }
        
        // In have-remote-offer state, we can't set another remote offer
        // We need to either create an answer first or reset the connection
        console.log(`[${currentUserId}] Cannot process new offer in have-remote-offer state, creating answer for existing offer`);
        
        try {
          // Check if we actually have a remote description before creating answer
          console.log(`[${currentUserId}] Checking remote description in have-remote-offer state:`, {
            hasRemoteDescription: !!peerConnectionRef.current.remoteDescription,
            remoteDescriptionType: peerConnectionRef.current.remoteDescription?.type,
            signalingState: peerConnectionRef.current.signalingState,
            connectionState: peerConnectionRef.current.connectionState
          });
          
          if (!peerConnectionRef.current.remoteDescription) {
            console.error(`[${currentUserId}] No remote description found in have-remote-offer state`);
            console.log(`[${currentUserId}] Setting the new offer as remote description`);
            
            // Set the new offer as remote description
            // Create RTCSessionDescription with proper CRLF normalization
            const normalizedOffer = new RTCSessionDescription({
              type: offer.type,
              sdp: offer.sdp?.replace(/\r?\n/g, '\r\n') || ''
            });
            console.log(`[${currentUserId}] Setting remote description (have-remote-offer) with normalized SDP:`, normalizedOffer.sdp.substring(0, 200) + '...');
            await peerConnectionRef.current.setRemoteDescription(normalizedOffer);
            hasProcessedOfferRef.current = true;
            
            // Now create answer
            console.log(`[${currentUserId}] Creating answer after setting remote description`);
            const isVideoCall = callInfo.type === 'video' || callNotification?.callType === 'video';
            const answer = await peerConnectionRef.current.createAnswer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: isVideoCall
            });
            
            // Clean the answer SDP and ensure consistent media ordering
            const cleanedSdp = normalizeSdp(answer.sdp || '');
            const finalSdp = isVideoCall ? ensureConsistentMediaOrder(cleanedSdp) : cleanedSdp;
            const processedAnswer = {
              ...answer,
              sdp: finalSdp
            };
            
            await peerConnectionRef.current.setLocalDescription(processedAnswer);
            
            if (signalingClientRef.current) {
              console.log(`[${currentUserId}] Sending answer to ${callInfo.callerId}`);
              signalingClientRef.current.sendAnswer(peerConnectionRef.current!.localDescription!, targetCallerId);
            }
          } else {
            // We already have a remote description and are in have-remote-offer state
            // This means we should already have created an answer, so we don't need to create another one
            console.log(`[${currentUserId}] Already have remote description in have-remote-offer state, skipping answer creation`);
            console.log(`[${currentUserId}] Current local description:`, peerConnectionRef.current.localDescription?.type);
            console.log(`[${currentUserId}] Current remote description:`, peerConnectionRef.current.remoteDescription?.type);
            
            // Mark as processed to avoid duplicate processing
            hasProcessedOfferRef.current = true;
          }
        } catch (error) {
          console.error(`[${currentUserId}] Failed to create answer for existing offer:`, error);
          
          // If we can't create an answer, reset the connection
          console.log(`[${currentUserId}] Resetting peer connection due to offer processing failure`);
          await forceResetPeerConnection();
        }
      } else if (currentState === 'have-local-offer') {
        console.warn(`[${currentUserId}] Received offer but in have-local-offer state - this shouldn't happen`);
        // This is a protocol violation, but try to handle gracefully
        try {
          // Create RTCSessionDescription with proper CRLF normalization
          const normalizedOffer = new RTCSessionDescription({
            type: offer.type,
            sdp: offer.sdp?.replace(/\r?\n/g, '\r\n') || ''
          });
          console.log(`[${currentUserId}] Setting remote description (have-local-offer) with normalized SDP:`, normalizedOffer.sdp.substring(0, 200) + '...');
          await peerConnectionRef.current.setRemoteDescription(normalizedOffer);
          hasProcessedOfferRef.current = true;
          
          const isVideoCall = callInfo.type === 'video' || callNotification?.callType === 'video';
          const answer = await peerConnectionRef.current.createAnswer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: isVideoCall
          });
          
          // SDP is used as-is from WebRTC, no cleaning needed
          
          await peerConnectionRef.current.setLocalDescription(answer);
          
          if (signalingClientRef.current) {
            signalingClientRef.current.sendAnswer(peerConnectionRef.current!.localDescription!, targetCallerId);
          }
        } catch (error) {
          console.error(`[${currentUserId}] Failed to process offer in have-local-offer state:`, error);
        }
      } else if (currentState === 'have-remote-pranswer') {
        console.log(`[${currentUserId}] Setting remote description (have-remote-pranswer)`);
        // Create RTCSessionDescription with proper CRLF normalization
        const normalizedOffer = new RTCSessionDescription({
          type: offer.type,
          sdp: offer.sdp?.replace(/\r?\n/g, '\r\n') || ''
        });
        console.log(`[${currentUserId}] Setting remote description (have-remote-pranswer) with normalized SDP:`, normalizedOffer.sdp.substring(0, 200) + '...');
        await peerConnectionRef.current.setRemoteDescription(normalizedOffer);
        hasProcessedOfferRef.current = true;
        
        const isVideoCall = callInfo.type === 'video' || callNotification?.callType === 'video';
        const answer = await peerConnectionRef.current.createAnswer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: isVideoCall
          });
        
        // Clean the answer SDP and ensure consistent media ordering
        const cleanedSdp = normalizeSdp(answer.sdp || '');
        const finalSdp = isVideoCall ? ensureConsistentMediaOrder(cleanedSdp) : cleanedSdp;
        const processedAnswer = {
          ...answer,
          sdp: finalSdp
        };
        
        await peerConnectionRef.current.setLocalDescription(processedAnswer);
        
        if (signalingClientRef.current) {
          signalingClientRef.current.sendAnswer(peerConnectionRef.current!.localDescription!, callInfo.callerId || '');
        }
      } else {
        console.warn(`[${currentUserId}] Unexpected signaling state for offer:`, currentState);
        // Try to process anyway for unknown states
        try {
          // Create RTCSessionDescription with proper CRLF normalization
          const normalizedOffer = new RTCSessionDescription({
            type: offer.type,
            sdp: offer.sdp?.replace(/\r?\n/g, '\r\n') || ''
          });
          console.log(`[${currentUserId}] Setting remote description (unknown state) with normalized SDP:`, normalizedOffer.sdp.substring(0, 200) + '...');
          await peerConnectionRef.current.setRemoteDescription(normalizedOffer);
          hasProcessedOfferRef.current = true;
          
          const isVideoCall = callInfo.type === 'video' || callNotification?.callType === 'video';
          const answer = await peerConnectionRef.current.createAnswer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: isVideoCall
          });
          
          // SDP is used as-is from WebRTC, no cleaning needed
          
          await peerConnectionRef.current.setLocalDescription(answer);
          
          if (signalingClientRef.current) {
            signalingClientRef.current.sendAnswer(peerConnectionRef.current!.localDescription!, targetCallerId);
          }
        } catch (error) {
          console.error(`[${currentUserId}] Failed to process offer in state ${currentState}:`, error);
        }
      }
    } catch (error) {
      console.error(`[${currentUserId}] Error handling offer:`, error);
      // Don't throw the error, just log it to prevent crashes
    }
  }, [currentUserId, createPeerConnectionWithDynamicICE, processPendingIceCandidates, callInfo.callerId]);

  const handleAnswer = useCallback(async (answer: RTCSessionDescriptionInit) => {
    console.log(`[${currentUserId}] Handling answer, peer connection exists:`, !!peerConnectionRef.current);
    console.log(`[${currentUserId}] Received answer SDP:`, answer.sdp?.substring(0, 200) + '...');
    console.log(`[${currentUserId}] Answer type:`, answer.type);
    console.log(`[${currentUserId}] Raw answer SDP (first 500 chars):`, answer.sdp?.substring(0, 500));
    
    // Validate answer structure
    if (!answer.type || !answer.sdp) {
      console.error(`[${currentUserId}] Invalid answer: missing type or sdp`, answer);
      return;
    }
    
    if (!peerConnectionRef.current) {
      console.warn(`[${currentUserId}] No peer connection available for answer, creating one...`);
      
      // Create peer connection if it doesn't exist
      try {
        peerConnectionRef.current = await createPeerConnectionWithDynamicICE();
      } catch (error) {
        console.error(`[${currentUserId}] Failed to create peer connection for answer:`, error);
        // Fallback to static configuration
        const configuration = {
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        };
        peerConnectionRef.current = new RTCPeerConnection(configuration);
        
        // Set up peer connection event handlers
        peerConnectionRef.current.onicecandidate = (event) => {
          if (event.candidate && signalingClientRef.current) {
            signalingClientRef.current.sendIceCandidate(event.candidate, callInfo.callerId || '');
          }
        };
      }

      peerConnectionRef.current.ontrack = (event) => {
        console.log(`[${currentUserId}] ===== ONTRACK EVENT TRIGGERED (HANDLE ANSWER) =====`);
        console.log(`[${currentUserId}] Event details:`, event);
        console.log(`[${currentUserId}] Event streams:`, event.streams);
        console.log(`[${currentUserId}] Event track:`, event.track);
        
        if (event.streams && event.streams[0]) {
          const remoteStream = event.streams[0];
          console.log(`[${currentUserId}] ===== REMOTE STREAM RECEIVED (HANDLE ANSWER) =====`);
          console.log(`[${currentUserId}] Stream ID:`, remoteStream.id);
          console.log(`[${currentUserId}] Stream active:`, remoteStream.active);
          console.log(`[${currentUserId}] Total tracks:`, remoteStream.getTracks().length);
          console.log(`[${currentUserId}] Audio tracks:`, remoteStream.getAudioTracks().length);
          console.log(`[${currentUserId}] Video tracks:`, remoteStream.getVideoTracks().length);
          
          // Log detailed track information and fix muted tracks
          const remoteTracks = remoteStream.getTracks();
          if (remoteTracks && Array.isArray(remoteTracks)) {
            remoteTracks.forEach((track, index) => {
              console.log(`[${currentUserId}] Track ${index} (handle answer, before fix):`, {
                kind: track.kind,
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState,
                label: track.label,
                id: track.id
              });
              
              // Fix muted audio tracks - check if muted property is writable
              if (track.kind === 'audio' && track.muted) {
                console.log(`[${currentUserId}] Fixing muted audio track ${index} (handle answer)`);
                try {
                  track.enabled = true;
                  console.log(`[${currentUserId}] Track ${index} (handle answer, after fix):`, {
                    kind: track.kind,
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState,
                    label: track.label,
                    id: track.id
                  });
                } catch (error) {
                  console.warn(`[${currentUserId}] Cannot set muted property on track ${index}:`, error);
                }
              }
            });
          } else {
            console.warn(`[${currentUserId}] No tracks available in remote stream or getTracks() returned invalid data:`, remoteTracks);
          }
          
          console.log(`[${currentUserId}] Setting remote stream in callInfo (handle answer)...`);
          setCallInfo(prev => {
            console.log(`[${currentUserId}] Previous callInfo (handle answer):`, prev);
            const newCallInfo = { ...prev, remoteStream: remoteStream };
            console.log(`[${currentUserId}] New callInfo (handle answer):`, newCallInfo);
            return newCallInfo;
          });
          console.log(`[${currentUserId}] ===== REMOTE STREAM SET COMPLETE (HANDLE ANSWER) =====`);
        } else {
          console.log(`[${currentUserId}] No streams in event or empty streams array (handle answer)`);
        }
      };

      peerConnectionRef.current.onconnectionstatechange = () => {
        if (!isEndingCallRef.current && callInfo.state !== 'idle') {
          console.log(`[${currentUserId}] Connection state changed to:`, peerConnectionRef.current?.connectionState, 'at', new Date().toISOString());
        }
        if (peerConnectionRef.current?.connectionState === 'connected' && !isEndingCallRef.current) {
          setCallInfo(prev => ({ ...prev, state: 'connected' }));
          if (callDurationIntervalRef.current) {
            clearInterval(callDurationIntervalRef.current);
          }
          callDurationIntervalRef.current = setInterval(() => {
            setCallInfo(prev => ({ ...prev, callDuration: prev.callDuration + 1 }));
          }, 1000);
        }
      };

      // Add debugging for ICE connection state
      peerConnectionRef.current.oniceconnectionstatechange = () => {
        console.log(`[${currentUserId}] ICE connection state:`, peerConnectionRef.current?.iceConnectionState);
      };

      // Add debugging for ICE gathering state
      peerConnectionRef.current.onicegatheringstatechange = () => {
        console.log(`[${currentUserId}] ICE gathering state:`, peerConnectionRef.current?.iceGatheringState);
      };
      
      console.log(`[${currentUserId}] Peer connection created for answer handling`);
      
      // Add local stream if it exists
      if (localStreamRef.current && peerConnectionRef.current) {
        console.log(`[${currentUserId}] Adding local stream to peer connection (handle answer)`);
        const localTracks = localStreamRef.current.getTracks();
        if (localTracks && Array.isArray(localTracks)) {
          localTracks.forEach(track => {
            try {
              if (!peerConnectionRef.current || !localStreamRef.current) {
                console.error(`[${currentUserId}] Cannot add track: peer connection or local stream is null`);
                return;
              }
              peerConnectionRef.current.addTrack(track, localStreamRef.current);
              console.log(`[${currentUserId}] Added track to peer connection (handle answer):`, track.kind);
            } catch (error) {
              console.error(`[${currentUserId}] Error adding track to peer connection (handle answer):`, error);
            }
          });
        } else {
          console.warn(`[${currentUserId}] No tracks available in local stream or getTracks() returned invalid data:`, localTracks);
        }
      }
    }

    // Check if we've already processed an answer
    if (hasProcessedAnswerRef.current) {
      console.warn(`[${currentUserId}] Answer already processed, ignoring duplicate`);
      return;
    }

    // Check if we're in a state where we can't process answers
    const currentState = peerConnectionRef.current.signalingState;
    if (currentState === 'have-remote-offer') {
      console.warn(`[${currentUserId}] In have-remote-offer state, cannot process answer - should create answer instead`);
      return;
    }

    try {
      // Check current signaling state (already declared above)
      console.log(`[${currentUserId}] Current signaling state:`, currentState);
      
      // Fix SDP M-line order to match offer
      // SDP M-line validation is no longer needed for audio-only calls

      // Comprehensive state validation for answer processing
      if (currentState === 'have-local-offer') {
        console.log(`[${currentUserId}] Setting remote description for answer (have-local-offer)`);
        
        // Set a timeout to prevent hanging states
        signalingStateTimeoutRef.current = setTimeout(() => {
          console.warn(`[${currentUserId}] Answer processing timeout - forcing peer connection reset`);
          forceResetPeerConnection();
        }, 10000); // 10 second timeout
        
      // Create RTCSessionDescription with proper CRLF normalization
      const normalizedAnswer = new RTCSessionDescription({
        type: answer.type as RTCSdpType,
        sdp: answer.sdp.replace(/\r?\n/g, '\r\n')
      });
      console.log(`[${currentUserId}] Setting remote description with normalized answer SDP:`, normalizedAnswer.sdp.substring(0, 200) + '...');
      await peerConnectionRef.current.setRemoteDescription(normalizedAnswer);
        hasProcessedAnswerRef.current = true;
        
        // Clear the timeout since we succeeded
        if (signalingStateTimeoutRef.current) {
          clearTimeout(signalingStateTimeoutRef.current);
          signalingStateTimeoutRef.current = null;
        }
        
        console.log(`[${currentUserId}] Answer processed successfully`);
        
        // Process any pending ICE candidates now that remote description is set
        await processPendingIceCandidates();
      } else if (currentState === 'stable') {
        console.warn(`[${currentUserId}] Peer connection in stable state - checking if answer is needed`);
        
        // Check if we already have a remote description
        const currentRemoteDescription = peerConnectionRef.current.remoteDescription;
        if (currentRemoteDescription) {
          console.log(`[${currentUserId}] Remote description already set, ignoring duplicate answer`);
          hasProcessedAnswerRef.current = true;
          return;
        }
        
        // Try to set the answer even in stable state
        try {
          console.log(`[${currentUserId}] Attempting to set answer in stable state`);
          // Create RTCSessionDescription with proper CRLF normalization
          const normalizedAnswer = new RTCSessionDescription({
            type: answer.type as RTCSdpType,
            sdp: answer.sdp.replace(/\r?\n/g, '\r\n')
          });
          console.log(`[${currentUserId}] Setting remote description (stable state) with normalized answer SDP:`, normalizedAnswer.sdp.substring(0, 200) + '...');
          await peerConnectionRef.current.setRemoteDescription(normalizedAnswer);
          hasProcessedAnswerRef.current = true;
          console.log(`[${currentUserId}] Answer set successfully in stable state`);
        } catch (stableError) {
          console.warn(`[${currentUserId}] Could not set answer in stable state:`, stableError instanceof Error ? stableError.message : String(stableError));
          
          // If it's the specific "stable state" error, try to recover
          if (stableError instanceof Error && stableError.message.includes('Called in wrong state: stable')) {
            console.log(`[${currentUserId}] Detected stable state error - attempting recovery`);
            
            // Check if we can proceed without setting the description
            const connectionState = peerConnectionRef.current.connectionState;
            const iceConnectionState = peerConnectionRef.current.iceConnectionState;
            
            console.log(`[${currentUserId}] Connection state: ${connectionState}, ICE state: ${iceConnectionState}`);
            
            // If we're already connected or connecting, we might not need to set the description
            if (connectionState === 'connected' || connectionState === 'connecting' || 
                iceConnectionState === 'connected' || iceConnectionState === 'completed') {
              console.log(`[${currentUserId}] Connection already established, skipping answer setting`);
              hasProcessedAnswerRef.current = true;
            } else {
              console.log(`[${currentUserId}] Connection not established, forcing peer connection reset`);
              forceResetPeerConnection();
            }
          } else {
            // Other errors - just mark as processed
            hasProcessedAnswerRef.current = true;
          }
        }
      } else if ((currentState as string) === 'have-remote-offer') {
        console.warn(`[${currentUserId}] Received answer but in have-remote-offer state - this shouldn't happen`);
        // This is a protocol violation - we should create an answer, not receive one
        console.log(`[${currentUserId}] Creating answer for existing offer instead of processing received answer`);
        
        try {
          // Create answer for the existing offer
          const isVideoCall = callInfo.type === 'video' || callNotification?.callType === 'video';
          const answerToSend = await peerConnectionRef.current.createAnswer({
            offerToReceiveAudio: true,
            offerToReceiveVideo: isVideoCall
          });
          await peerConnectionRef.current.setLocalDescription(answerToSend);
          
          if (signalingClientRef.current) {
            signalingClientRef.current.sendAnswer(peerConnectionRef.current!.localDescription!);
          }
          
          hasProcessedAnswerRef.current = true;
          console.log(`[${currentUserId}] Answer created and sent for existing offer`);
        } catch (error) {
          console.error(`[${currentUserId}] Failed to create answer for existing offer:`, error);
          
          // Reset connection if we can't create an answer
          console.log(`[${currentUserId}] Resetting peer connection due to answer creation failure`);
          await forceResetPeerConnection();
        }
      } else if (currentState === 'have-local-pranswer') {
        console.log(`[${currentUserId}] Setting remote description for answer (have-local-pranswer)`);
        // Create RTCSessionDescription with proper CRLF normalization
        const normalizedAnswer = new RTCSessionDescription({
          type: answer.type as RTCSdpType,
          sdp: answer.sdp.replace(/\r?\n/g, '\r\n')
        });
        console.log(`[${currentUserId}] Setting remote description (have-local-pranswer) with normalized answer SDP:`, normalizedAnswer.sdp.substring(0, 200) + '...');
        await peerConnectionRef.current.setRemoteDescription(normalizedAnswer);
        hasProcessedAnswerRef.current = true;
        console.log(`[${currentUserId}] Answer processed successfully`);
        
        // Process any pending ICE candidates now that remote description is set
        await processPendingIceCandidates();
      } else if (currentState === 'have-remote-pranswer') {
        console.log(`[${currentUserId}] Setting remote description for answer (have-remote-pranswer)`);
        // Create RTCSessionDescription with proper CRLF normalization
        const normalizedAnswer = new RTCSessionDescription({
          type: answer.type as RTCSdpType,
          sdp: answer.sdp.replace(/\r?\n/g, '\r\n')
        });
        console.log(`[${currentUserId}] Setting remote description (have-remote-pranswer) with normalized answer SDP:`, normalizedAnswer.sdp.substring(0, 200) + '...');
        await peerConnectionRef.current.setRemoteDescription(normalizedAnswer);
        hasProcessedAnswerRef.current = true;
        console.log(`[${currentUserId}] Answer processed successfully`);
        
        // Process any pending ICE candidates now that remote description is set
        await processPendingIceCandidates();
      } else {
        console.warn(`[${currentUserId}] Unexpected signaling state for answer:`, currentState);
        // Try to set anyway for unknown states
        try {
          // Create RTCSessionDescription with proper CRLF normalization
          const normalizedAnswer = new RTCSessionDescription({
            type: answer.type as RTCSdpType,
            sdp: answer.sdp.replace(/\r?\n/g, '\r\n')
          });
          console.log(`[${currentUserId}] Setting remote description (unknown state) with normalized answer SDP:`, normalizedAnswer.sdp.substring(0, 200) + '...');
          await peerConnectionRef.current.setRemoteDescription(normalizedAnswer);
          hasProcessedAnswerRef.current = true;
          console.log(`[${currentUserId}] Answer processed in unexpected state:`, currentState);
        } catch (error) {
          console.error(`[${currentUserId}] Failed to process answer in state ${currentState}:`, error);
        }
      }
    } catch (error) {
      console.error(`[${currentUserId}] Error handling answer:`, error);
      // Don't throw the error, just log it to prevent crashes
    }
  }, [currentUserId, createPeerConnectionWithDynamicICE, processPendingIceCandidates, forceResetPeerConnection]);

  const handleIceCandidate = useCallback(async (candidate: RTCIceCandidateInit) => {
    if (!peerConnectionRef.current) {
      console.warn(`[${currentUserId}] No peer connection available for ICE candidate`);
      return;
    }

    // Validate candidate before processing
    if (!candidate) {
      console.warn(`[${currentUserId}] Null/undefined ICE candidate received`);
      return;
    }
    
    // Check if candidate has required fields
    if (!candidate.candidate && candidate.candidate !== '') {
      console.warn(`[${currentUserId}] ICE candidate missing 'candidate' field:`, candidate);
      return;
    }
    
    // Check if candidate is not an end-of-candidates marker
    if (candidate.candidate === '') {
      console.log(`[${currentUserId}] End-of-candidates ICE candidate received`);
    } else {
      console.log(`[${currentUserId}] ICE candidate received:`, {
        candidate: candidate.candidate?.substring(0, 50) + '...',
        sdpMLineIndex: candidate.sdpMLineIndex,
        sdpMid: candidate.sdpMid
      });
    }

    // Check if peer connection is in a valid state for adding ICE candidates
    const signalingState = peerConnectionRef.current.signalingState;
    const connectionState = peerConnectionRef.current.connectionState;
    const iceConnectionState = peerConnectionRef.current.iceConnectionState;
    
    console.log(`[${currentUserId}] ICE candidate received - States:`, {
      signaling: signalingState,
      connection: connectionState,
      iceConnection: iceConnectionState
    });

    // Don't add ICE candidates if peer connection is closed or failed
    if (signalingState === 'closed' || connectionState === 'closed' || connectionState === 'failed') {
      console.warn(`[${currentUserId}] Peer connection is closed/failed, ignoring ICE candidate`);
      return;
    }

    // Don't add ICE candidates if we don't have a remote description yet
    if (!peerConnectionRef.current.remoteDescription) {
      console.warn(`[${currentUserId}] No remote description set yet, queuing ICE candidate`);
      // Store candidate for later processing
      if (!pendingIceCandidatesRef.current) {
        pendingIceCandidatesRef.current = [];
      }
      pendingIceCandidatesRef.current.push(candidate);
      return;
    }

    // Don't add ICE candidates if we're in the wrong signaling state
    if (signalingState !== 'have-local-offer' && signalingState !== 'have-remote-offer' && signalingState !== 'stable') {
      console.warn(`[${currentUserId}] Cannot add ICE candidate in signaling state: ${signalingState}`);
      return;
    }

    try {
      console.log(`[${currentUserId}] Adding ICE candidate, signaling state:`, signalingState);
      
      // Additional validation before adding (signalingState is already validated above)
      
      await peerConnectionRef.current.addIceCandidate(candidate);
      console.log(`[${currentUserId}] ICE candidate added successfully`);
    } catch (error) {
      console.error(`[${currentUserId}] Error handling ICE candidate:`, error);
      console.error(`[${currentUserId}] Candidate details:`, {
        candidate: candidate.candidate?.substring(0, 100),
        sdpMLineIndex: candidate.sdpMLineIndex,
        sdpMid: candidate.sdpMid
      });
      
      if (error instanceof Error && error.name === 'InvalidStateError') {
        console.warn(`[${currentUserId}] Invalid state for adding ICE candidate, signaling state:`, signalingState);
        console.warn(`[${currentUserId}] Connection state:`, connectionState);
        console.warn(`[${currentUserId}] ICE connection state:`, iceConnectionState);
      } else if (error instanceof Error && error.name === 'OperationError') {
        console.warn(`[${currentUserId}] Operation error adding ICE candidate - candidate may be invalid or duplicate`);
        console.warn(`[${currentUserId}] This is often caused by duplicate ICE candidates or invalid SDP`);
      } else if (error instanceof Error && error.name === 'TypeError') {
        console.warn(`[${currentUserId}] TypeError adding ICE candidate - candidate format may be wrong`);
      } else {
        console.warn(`[${currentUserId}] Unknown error adding ICE candidate:`, error instanceof Error ? error.name : 'Unknown', error instanceof Error ? error.message : String(error));
      }
      
      // Don't throw the error, just log it to prevent crashes
    }
  }, [currentUserId]);

  const initiateCall = useCallback(async (type: 'audio' | 'video') => {
    console.log(`[${currentUserId}] Initiating ${type} call`);

    try {
      setCallInfo(prev => ({ ...prev, state: 'initiating', type, error: null }));

      // Get user media
      const constraints = {
        audio: true,
        video: type === 'video'
      };

      console.log(`[${currentUserId}] Requesting user media with constraints:`, constraints);
      
      const stream = await getUserMedia(constraints);
      localStreamRef.current = stream;
      console.log(`[${currentUserId}] ===== LOCAL STREAM OBTAINED (INITIATE CALL) =====`);
      console.log(`[${currentUserId}] Stream ID:`, stream.id);
      console.log(`[${currentUserId}] Stream active:`, stream.active);
      console.log(`[${currentUserId}] Total tracks:`, stream.getTracks().length);
      console.log(`[${currentUserId}] Track kinds:`, stream.getTracks().map(t => t.kind));
      console.log(`[${currentUserId}] Audio tracks:`, stream.getAudioTracks().length);
      console.log(`[${currentUserId}] Video tracks:`, stream.getVideoTracks().length);
      
      // Additional mobile video debugging
      if (type === 'video') {
        const videoTracks = stream.getVideoTracks();
        console.log(`[${currentUserId}] ===== VIDEO TRACK DEBUG (MOBILE) =====`);
        console.log(`[${currentUserId}] Video track count:`, videoTracks.length);
        videoTracks.forEach((track, index) => {
          console.log(`[${currentUserId}] Video track ${index}:`, {
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            label: track.label,
            kind: track.kind,
            id: track.id,
            settings: track.getSettings()
          });
        });
        
        if (videoTracks.length === 0) {
          console.error(`[${currentUserId}] ❌ NO VIDEO TRACKS FOUND FOR VIDEO CALL!`);
        } else {
          console.log(`[${currentUserId}] ✅ Video tracks found for video call`);
        }
      }
      
      // Log detailed track information
      const tracks = stream.getTracks();
      if (tracks && Array.isArray(tracks)) {
        tracks.forEach((track, index) => {
          console.log(`[${currentUserId}] Local track ${index}:`, {
            kind: track.kind,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            label: track.label,
            id: track.id
          });
        });
      } else {
        console.warn(`[${currentUserId}] No tracks available in stream or getTracks() returned invalid data:`, tracks);
      }
      
      console.log(`[${currentUserId}] Setting local stream in callInfo (initiate call)...`);
      setCallInfo(prev => {
        console.log(`[${currentUserId}] Previous callInfo (local initiate):`, prev);
        const newCallInfo = { ...prev, localStream: stream };
        console.log(`[${currentUserId}] New callInfo (local initiate):`, newCallInfo);
        return newCallInfo;
      });
      console.log(`[${currentUserId}] ===== LOCAL STREAM SET COMPLETE (INITIATE CALL) =====`);

      // Create peer connection with dynamic ICE servers
      try {
        peerConnectionRef.current = await createPeerConnectionWithDynamicICE();
      } catch (error) {
        console.error(`[${currentUserId}] Failed to create peer connection for initiate call:`, error);
        // Fallback to static configuration
        const configuration = {
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
        };
        peerConnectionRef.current = new RTCPeerConnection(configuration);
      }

      // Add local stream to peer connection
      if (peerConnectionRef.current) {
        const tracks = stream.getTracks();
        if (tracks && Array.isArray(tracks)) {
          tracks.forEach(track => {
            try {
              if (!peerConnectionRef.current) {
                console.error(`[${currentUserId}] Cannot add track: peer connection is null`);
                return;
              }
              peerConnectionRef.current.addTrack(track, stream);
              console.log(`[${currentUserId}] Added track to peer connection:`, track.kind);
            } catch (error) {
              console.error(`[${currentUserId}] Error adding track to peer connection:`, error);
            }
          });
        } else {
          console.warn(`[${currentUserId}] No tracks available in stream or getTracks() returned invalid data:`, tracks);
        }
      } else {
        console.error(`[${currentUserId}] Peer connection is null when trying to add tracks`);
      }

      // Set up peer connection event handlers
      peerConnectionRef.current.onicecandidate = (event) => {
        if (event.candidate && signalingClientRef.current) {
          signalingClientRef.current.sendIceCandidate(event.candidate, callInfo.callerId || '');
        }
      };

      peerConnectionRef.current.ontrack = (event) => {
        console.log(`[${currentUserId}] ===== ONTRACK EVENT TRIGGERED (INITIATE CALL) =====`);
        console.log(`[${currentUserId}] Event details:`, event);
        console.log(`[${currentUserId}] Event streams:`, event.streams);
        console.log(`[${currentUserId}] Event track:`, event.track);
        
        if (event.streams && event.streams[0]) {
          const remoteStream = event.streams[0];
          console.log(`[${currentUserId}] ===== REMOTE STREAM RECEIVED (INITIATE CALL) =====`);
          console.log(`[${currentUserId}] Stream ID:`, remoteStream.id);
          console.log(`[${currentUserId}] Stream active:`, remoteStream.active);
          console.log(`[${currentUserId}] Total tracks:`, remoteStream.getTracks().length);
          console.log(`[${currentUserId}] Audio tracks:`, remoteStream.getAudioTracks().length);
          
          // Log detailed track information and fix muted tracks
          const remoteTracks = remoteStream.getTracks();
          if (remoteTracks && Array.isArray(remoteTracks)) {
            remoteTracks.forEach((track, index) => {
              console.log(`[${currentUserId}] Track ${index} (before fix):`, {
                kind: track.kind,
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState,
                label: track.label,
                id: track.id
              });
              
              // Fix muted audio tracks - check if muted property is writable
              if (track.kind === 'audio' && track.muted) {
                console.log(`[${currentUserId}] Fixing muted audio track ${index}`);
                try {
                  track.enabled = true;
                  console.log(`[${currentUserId}] Track ${index} (after fix):`, {
                    kind: track.kind,
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState,
                    label: track.label,
                    id: track.id
                  });
                } catch (error) {
                  console.warn(`[${currentUserId}] Cannot set muted property on track ${index}:`, error);
                }
              }
            });
          } else {
            console.warn(`[${currentUserId}] No tracks available in remote stream or getTracks() returned invalid data:`, remoteTracks);
          }
          
          console.log(`[${currentUserId}] Setting remote stream in callInfo (initiate call)...`);
          setCallInfo(prev => {
            console.log(`[${currentUserId}] Previous callInfo (initiate):`, prev);
            const newCallInfo = { ...prev, remoteStream: remoteStream };
            console.log(`[${currentUserId}] New callInfo (initiate):`, newCallInfo);
            return newCallInfo;
          });
          console.log(`[${currentUserId}] ===== REMOTE STREAM SET COMPLETE (INITIATE CALL) =====`);
        } else {
          console.log(`[${currentUserId}] No streams in event or empty streams array (initiate call)`);
        }
      };

      peerConnectionRef.current.onconnectionstatechange = () => {
        // Don't log if we're ending the call or if call is already ended
        if (!isEndingCallRef.current && callInfo.state !== 'idle') {
          console.log(`[${currentUserId}] Connection state changed to:`, peerConnectionRef.current?.connectionState, 'at', new Date().toISOString());
        }
        if (peerConnectionRef.current?.connectionState === 'connected' && !isEndingCallRef.current) {
          setCallInfo(prev => ({ ...prev, state: 'connected' }));
          // Start call duration timer (clear any existing timer first)
          if (callDurationIntervalRef.current) {
            clearInterval(callDurationIntervalRef.current);
          }
          callDurationIntervalRef.current = setInterval(() => {
            setCallInfo(prev => ({ ...prev, callDuration: prev.callDuration + 1 }));
          }, 1000);
        }
      };

      // Add debugging for ICE connection state
      peerConnectionRef.current.oniceconnectionstatechange = () => {
        console.log(`[${currentUserId}] ICE connection state:`, peerConnectionRef.current?.iceConnectionState);
      };

      // Add debugging for ICE gathering state
      peerConnectionRef.current.onicegatheringstatechange = () => {
        console.log(`[${currentUserId}] ICE gathering state:`, peerConnectionRef.current?.iceGatheringState);
      };

      // Send call request
      if (signalingClientRef.current) {
        const success = signalingClientRef.current.sendCallRequest({
          callerId: currentUserId,
          callerName: currentUserName,
          callerPhoto: currentUserPhoto,
          callType: type
        });
        
        if (!success) {
          console.error(`[${currentUserId}] Failed to send call request`);
        }
      }

      setCallInfo(prev => ({ ...prev, state: 'ringing' }));

      // Offer will be sent when call is accepted by receiver

    } catch (error) {
      console.error(`[${currentUserId}] Error initiating call:`, error);
      setCallInfo(prev => ({ ...prev, error: 'Failed to initiate call' }));
    }
  }, [currentUserId, currentUserName, currentUserPhoto]);

  const acceptCall = useCallback(async () => {
    if (!callNotification) return;

    console.log(`[${currentUserId}] Accepting call from:`, callNotification.callerId);

    try {
      setCallInfo(prev => ({ 
        ...prev, 
        state: 'connecting', 
        type: callNotification.callType,
        callerId: callNotification.callerId,
        callerName: callNotification.callerName,
        callerPhoto: callNotification.callerPhoto,
        error: null 
      }));

      setCallNotification(null);
      console.log(`[${currentUserId}] Call notification cleared, peer connection already exists:`, !!peerConnectionRef.current);

      // Get user media
      const constraints = {
        audio: true,
        video: callNotification.callType === 'video'
      };

      console.log(`[${currentUserId}] Requesting user media with constraints:`, constraints);
      const stream = await getUserMedia(constraints);
      localStreamRef.current = stream;
      console.log(`[${currentUserId}] ===== LOCAL STREAM OBTAINED (ACCEPT CALL) =====`);
      console.log(`[${currentUserId}] Stream ID:`, stream.id);
      console.log(`[${currentUserId}] Stream active:`, stream.active);
      console.log(`[${currentUserId}] Total tracks:`, stream.getTracks().length);
      console.log(`[${currentUserId}] Track kinds:`, stream.getTracks().map(t => t.kind));
      console.log(`[${currentUserId}] Audio tracks:`, stream.getAudioTracks().length);
      console.log(`[${currentUserId}] Video tracks:`, stream.getVideoTracks().length);
      
      // Additional mobile video debugging for accepted calls
      if (callNotification?.callType === 'video') {
        const videoTracks = stream.getVideoTracks();
        console.log(`[${currentUserId}] ===== VIDEO TRACK DEBUG (ACCEPT CALL - MOBILE) =====`);
        console.log(`[${currentUserId}] Video track count:`, videoTracks.length);
        videoTracks.forEach((track, index) => {
          console.log(`[${currentUserId}] Video track ${index}:`, {
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
            label: track.label,
            kind: track.kind,
            id: track.id,
            settings: track.getSettings()
          });
        });
        
        if (videoTracks.length === 0) {
          console.error(`[${currentUserId}] ❌ NO VIDEO TRACKS FOUND FOR VIDEO CALL ACCEPT!`);
        } else {
          console.log(`[${currentUserId}] ✅ Video tracks found for video call accept`);
        }
      }
      
      // Log detailed track information
      stream.getTracks().forEach((track, index) => {
        console.log(`[${currentUserId}] Local track ${index} (accept):`, {
          kind: track.kind,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
          label: track.label,
          id: track.id
        });
      });
      
      console.log(`[${currentUserId}] Setting local stream in callInfo (accept call)...`);
      setCallInfo(prev => {
        console.log(`[${currentUserId}] Previous callInfo (local accept):`, prev);
        const newCallInfo = { ...prev, localStream: stream };
        console.log(`[${currentUserId}] New callInfo (local accept):`, newCallInfo);
        return newCallInfo;
      });
      console.log(`[${currentUserId}] ===== LOCAL STREAM SET COMPLETE (ACCEPT CALL) =====`);
      console.log(`[${currentUserId}] User media obtained, stream tracks:`, stream.getTracks().length);

      // Create peer connection if it doesn't exist
      if (!peerConnectionRef.current) {
        console.log(`[${currentUserId}] Creating peer connection for accept call`);
        try {
          peerConnectionRef.current = await createPeerConnectionWithDynamicICE();
        } catch (error) {
          console.error(`[${currentUserId}] Failed to create peer connection for accept call:`, error);
          // Fallback to static configuration
          const configuration = createFallbackRTCConfiguration();
          peerConnectionRef.current = new RTCPeerConnection(configuration);
        }
      }

      // Add local stream to peer connection
      if (peerConnectionRef.current) {
        const tracks = stream.getTracks();
        if (tracks && Array.isArray(tracks)) {
          tracks.forEach(track => {
            try {
              if (!peerConnectionRef.current) {
                console.error(`[${currentUserId}] Cannot add track: peer connection is null`);
                return;
              }
              peerConnectionRef.current.addTrack(track, stream);
              console.log(`[${currentUserId}] Added track to peer connection (accept call):`, track.kind);
            } catch (error) {
              console.error(`[${currentUserId}] Error adding track to peer connection (accept call):`, error);
            }
          });
          console.log(`[${currentUserId}] Added local stream to peer connection`);
        } else {
          console.warn(`[${currentUserId}] No tracks available in stream or getTracks() returned invalid data:`, tracks);
        }
      } else {
        console.error(`[${currentUserId}] Failed to create peer connection for adding stream`);
      }

      // Send call-accepted message to notify caller that we're ready
      if (signalingClientRef.current) {
        console.log(`[${currentUserId}] Sending call-accepted message`);
        signalingClientRef.current.send({
          type: 'call-accepted',
          calleeId: currentUserId
        });
      }

      // Wait for offer from caller - it should be handled in handleOffer
      console.log(`[${currentUserId}] Waiting for offer from caller`);

    } catch (error) {
      console.error(`[${currentUserId}] Error accepting call:`, error);
      setCallInfo(prev => ({ ...prev, error: 'Failed to accept call' }));
    }
  }, [callNotification, currentUserId]);

  const rejectCall = useCallback(() => {
    console.log(`[${currentUserId}] Rejecting call`);
    
    // Clear call duration interval
    if (callDurationIntervalRef.current) {
      clearInterval(callDurationIntervalRef.current);
      callDurationIntervalRef.current = null;
    }

    // Clear offer timeout
    if (offerTimeoutRef.current) {
      clearTimeout(offerTimeoutRef.current);
      offerTimeoutRef.current = null;
    }
    
    // Clear peer connection and event handlers
    if (peerConnectionRef.current) {
      peerConnectionRef.current.onicecandidate = null;
      peerConnectionRef.current.ontrack = null;
      peerConnectionRef.current.onconnectionstatechange = null;
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    
    setCallNotification(null);
    
    if (signalingClientRef.current) {
      signalingClientRef.current.sendCallEnded();
    }
    
    // Reset the ending flags
    isEndingCallRef.current = false;
    isEndingDueToMessageRef.current = false;
    resetSignalingState();
  }, [currentUserId]);

  const endCall = useCallback(() => {
    console.log(`[${currentUserId}] endCall called - current state:`, callInfo.state, 'isEnding:', isEndingCallRef.current);
    
    // Simple protection: only prevent if already ending
    if (isEndingCallRef.current) {
      console.log(`[${currentUserId}] Call already ending, ignoring duplicate call`);
      return;
    }

    isEndingCallRef.current = true;
    console.log(`[${currentUserId}] Ending call - proceeding with cleanup`);

    // Clear call duration interval
    stopCallDuration(callDurationIntervalRef);

    // Clear offer timeout
    if (offerTimeoutRef.current) {
      clearTimeout(offerTimeoutRef.current);
      offerTimeoutRef.current = null;
    }

    // Close peer connection and clear event handlers
    if (peerConnectionRef.current) {
      // Clear all event handlers to prevent memory leaks
      peerConnectionRef.current.onicecandidate = null;
      peerConnectionRef.current.ontrack = null;
      peerConnectionRef.current.onconnectionstatechange = null;
      
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }

    // Send call ended only if we're not ending due to receiving a call-ended message
    if (signalingClientRef.current && !isEndingDueToMessageRef.current) {
      console.log(`[${currentUserId}] Sending call-ended message`);
      const success = signalingClientRef.current.sendCallEnded();
      console.log(`[${currentUserId}] Call-ended message send result:`, success);
    } else if (isEndingDueToMessageRef.current) {
      console.log(`[${currentUserId}] Not sending call-ended message (ending due to received message)`);
    } else {
      console.error(`[${currentUserId}] No signaling client available to send call-ended message`);
    }

    // Reset call info
    console.log(`[${currentUserId}] Resetting call info to idle state`);
    setCallInfo({
      state: 'idle',
      type: null,
      callerId: null,
      callerName: null,
      callerPhoto: undefined,
      callDuration: 0,
      error: null,
      localStream: null,
      remoteStream: null,
      isMuted: false,
      isVideoOn: true,
      isIncoming: false,
      isSpeakerOn: false
    });

    setCallNotification(null);
    
    // Reset the ending flags and processing flags
    isEndingCallRef.current = false;
    isEndingDueToMessageRef.current = false;
    resetSignalingState();
    console.log(`[${currentUserId}] Call ended successfully, state reset to idle`);
  }, [currentUserId]);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const audioTracks = localStreamRef.current.getAudioTracks();
      // Use callInfo state instead of local state for consistency
      const currentMutedState = callInfo.isMuted ?? false;
      const newMutedState = !currentMutedState;
      audioTracks.forEach(track => {
        track.enabled = !newMutedState;
      });
      setIsMuted(newMutedState);
      // Also update callInfo state for ConnectedCallInterface
      setCallInfo(prev => ({ ...prev, isMuted: newMutedState }));
    }
  }, [callInfo.isMuted]);

  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTracks = localStreamRef.current.getVideoTracks();
      // Use callInfo state instead of local state for consistency
      const currentVideoState = callInfo.isVideoOn ?? true;
      const newVideoState = !currentVideoState;
      videoTracks.forEach(track => {
        track.enabled = newVideoState;
      });
      setIsVideoOn(newVideoState);
      // Also update callInfo state for ConnectedCallInterface
      setCallInfo(prev => ({ ...prev, isVideoOn: newVideoState }));
    }
  }, [callInfo.isVideoOn]);

  const toggleSpeaker = useCallback(() => {
    setIsSpeakerOn(!isSpeakerOn);
    // Note: Speaker control would need to be implemented with audio element
    // This is a placeholder for the UI state
  }, [isSpeakerOn]);

  const testAudioPermissions = useCallback(async () => {
    console.log(`[${currentUserId}] Testing audio permissions...`);
    try {
      const stream = await getUserMedia({ audio: true });
      console.log(`[${currentUserId}] Audio permissions granted, stream:`, stream);
      console.log(`[${currentUserId}] Audio tracks:`, stream.getAudioTracks());
      
      // Test if we can play audio
      const audio = new Audio();
      audio.srcObject = stream;
      audio.muted = true; // Mute to prevent echo
      await audio.play();
      console.log(`[${currentUserId}] Audio playback test successful`);
      
      // Clean up
      stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      console.error(`[${currentUserId}] Audio permission test failed:`, error);
    }
  }, [currentUserId]);

  const switchCamera = useCallback(() => {
    // Note: Camera switching would need to be implemented with getUserMedia
    // This is a placeholder for the UI state
    console.log('Camera switch requested');
  }, []);

  const contextValue: CallContextType = {
    callInfo,
    callNotification,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    toggleSpeaker,
    switchCamera,
    testAudioPermissions,
    isCallActive: (() => {
      const active = callInfo.state === 'connected' || callInfo.state === 'connecting' || callInfo.state === 'ringing';
      if (callInfo.state !== 'idle') {
        console.log(`[${currentUserId}] isCallActive:`, active, 'state:', callInfo.state);
      }
      return active;
    })(),
    isIncomingCall: callNotification !== null,
    isOutgoingCall: callInfo.state === 'ringing' || callInfo.state === 'connecting',
    // Additional properties for ChatWindow compatibility
    callType: callNotification ? callNotification.callType : callInfo.type,
    isIncoming: callNotification !== null,
    callerName: callNotification ? callNotification.callerName : callInfo.callerName,
    callerPhoto: callNotification ? callNotification.callerPhoto : callInfo.callerPhoto,
    isConnected: callInfo.state === 'connected',
    isMuted,
    isVideoOn,
    isSpeakerOn,
    callDuration: callInfo.callDuration,
    localStream: callInfo.localStream,
    remoteStream: callInfo.remoteStream
  };

  return (
    <CallContext.Provider value={contextValue}>
      {children}
    </CallContext.Provider>
  );
};
